import axios from 'axios';
import { useLocale } from '../i18n/LocaleProvider';
import {
  MIN_PASSWORD_LENGTH,
  authRefusalKey,
  passwordProblem,
  passwordProblemKey,
  type NewPasswordDraft,
} from '../lib/operator-account';
import { opsErrorMessage } from '../lib/ops-api';

/**
 * The "new password" + "repeat" pair, shared by the forced change after a one-time password and
 * the account page, with the policy hint under it.
 *
 * The hint appears only once something is typed: a red "too short" under an empty box is noise.
 */
export function NewPasswordFields({
  draft,
  onNext,
  onConfirm,
}: {
  readonly draft: NewPasswordDraft;
  readonly onNext: (value: string) => void;
  readonly onConfirm: (value: string) => void;
}) {
  const { t } = useLocale();
  const problem = draft.next ? passwordProblem(draft) : null;
  // A mismatch is only worth saying once the second box has something in it.
  const shown = problem === 'MISMATCH' && !draft.confirm ? null : problem;

  return (
    <>
      <div>
        <label className="field-label" htmlFor="new-password">
          {t('account.newPassword')}
        </label>
        <input
          id="new-password"
          type="password"
          value={draft.next}
          onChange={(e) => onNext(e.target.value)}
          required
          autoComplete="new-password"
          className="field-input"
          aria-describedby="new-password-hint"
        />
        <p id="new-password-hint" className="text-xs text-gray-500 mt-1">
          {t('account.policy', { min: MIN_PASSWORD_LENGTH })}
        </p>
      </div>
      <div>
        <label className="field-label" htmlFor="confirm-password">
          {t('account.confirmPassword')}
        </label>
        <input
          id="confirm-password"
          type="password"
          value={draft.confirm}
          onChange={(e) => onConfirm(e.target.value)}
          required
          autoComplete="new-password"
          className="field-input"
        />
        {shown && (
          <p className="text-xs text-red-700 mt-1" role="status">
            {t(passwordProblemKey(shown), { min: MIN_PASSWORD_LENGTH })}
          </p>
        )}
      </div>
    </>
  );
}

/**
 * The text for a refused sign-in or password change: the operator's language when the API said
 * which rule was broken (locked, too short, …), otherwise the API's own message.
 */
export function useAuthErrorText(): (error: unknown) => string {
  const { t } = useLocale();
  return (error: unknown) => {
    const key = axios.isAxiosError(error)
      ? authRefusalKey(error.response?.data)
      : null;
    return key
      ? t(key, { min: MIN_PASSWORD_LENGTH })
      : opsErrorMessage(error, t('login.failed'));
  };
}
